import i18n from "../i18n";
// admin.service.ts
import { AxiosResponse } from "axios";
import api from "./api";
import {
  IAdmin,
  ICreateAdminRequest,
  IUpdateAdminRequest,
  IChangePasswordRequest,
  IGetAdminsResponse,
  IAdminResponse,
  IStaffRole,
  IStaffRoleCreate,
  IStaffRoleUpdate,
  IStaffRoleResponse,
  IStaffRolesResponse,
} from "../types/admin.types";

class AdminService {
  /**
   * Get all admins
   */
  public async getAllAdmins(): Promise<IAdmin[]> {
    try {
      const response: AxiosResponse<IGetAdminsResponse> =
        await api.get<IGetAdminsResponse>("/admins/administrators");
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchAdmins"));
    }
  }

  /**
   * Get specific admin by ID
   */
  public async getAdminById(adminId: string): Promise<IAdmin> {
    try {
      const response: AxiosResponse<IAdminResponse> =
        await api.get<IAdminResponse>(`/admins/administrators/${adminId}`);
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchAdminDetails"));
    }
  }

  /**
   * Get current logged-in admin profile
   */
  public async getCurrentAdmin(): Promise<IAdmin> {
    try {
      const response: AxiosResponse<IAdminResponse> =
        await api.get<IAdminResponse>("/admins/me");
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchCurrentAdminProfile"));
    }
  }

  /**
   * Alias for getCurrentAdmin (for Profile page)
   */
  public async getProfile(): Promise<IAdmin> {
    return this.getCurrentAdmin();
  }

  /**
   * Create new admin (super_admin only)
   */
  public async createAdmin(adminData: ICreateAdminRequest): Promise<IAdmin> {
    try {
      const response: AxiosResponse<IAdminResponse> =
        await api.post<IAdminResponse>("/admins/administrators", adminData);
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.createAdmin"));
    }
  }

  /**
   * Update admin details
   */
  public async updateAdmin(
    adminId: string,
    adminData: IUpdateAdminRequest
  ): Promise<IAdmin> {
    try {
      const response: AxiosResponse<IAdminResponse> =
        await api.patch<IAdminResponse>(
          `/admins/administrators/${adminId}`,
          adminData
        );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.updateAdmin"));
    }
  }

  /**
   * Change admin password
   */
  public async changePassword(
    adminId: string,
    passwordData: IChangePasswordRequest
  ): Promise<{ message: string }> {
    try {
      const response = await api.patch(
        `/admins/administrators/${adminId}/password`,
        passwordData
      );
      return response.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.changePasswordFailed"));
    }
  }

  /**
   * Delete admin (super_admin only)
   */
  public async deleteAdmin(adminId: string): Promise<{ message: string }> {
    try {
      const response = await api.delete(`/admins/administrators/${adminId}`);
      return response.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.deleteAdmin"));
    }
  }

  /**
   * Activate/Deactivate admin
   */
  public async toggleAdminStatus(
    adminId: string,
    active: boolean
  ): Promise<IAdmin> {
    try {
      const response: AxiosResponse<IAdminResponse> =
        await api.patch<IAdminResponse>(
          `/admins/administrators/${adminId}/status`,
          { active }
        );
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.updateAdminStatus"));
    }
  }

  /**
   * Get active admins only
   */
  public async getActiveAdmins(): Promise<IAdmin[]> {
    try {
      const allAdmins = await this.getAllAdmins();
      return allAdmins.filter((admin) => admin.active);
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchActiveAdmins"));
    }
  }

  /**
   * Get admins by role name slug (e.g. "super_admin", "relationship_manager")
   */
  public async getAdminsByRole(role: string): Promise<IAdmin[]> {
    try {
      const allAdmins = await this.getAllAdmins();
      return allAdmins.filter((admin) => admin.role.name === role);
    } catch (error: any) {
      this.handleError(error, `Failed to fetch ${role}s`);
    }
  }

  // ---------------------------------------------------------------------------
  // StaffRole management (super_admin only for mutations)
  // ---------------------------------------------------------------------------

  /**
   * List all StaffRole documents
   */
  public async listRoles(): Promise<IStaffRole[]> {
    try {
      const response: AxiosResponse<IStaffRolesResponse> =
        await api.get<IStaffRolesResponse>("/admins/roles");
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.fetchRoles"));
    }
  }

  /**
   * Create a new StaffRole (super_admin only)
   */
  public async createRole(data: IStaffRoleCreate): Promise<IStaffRole> {
    try {
      const response: AxiosResponse<IStaffRoleResponse> =
        await api.post<IStaffRoleResponse>("/admins/roles", data);
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.createRole"));
    }
  }

  /**
   * Update a StaffRole (super_admin only)
   */
  public async updateRole(id: string, data: IStaffRoleUpdate): Promise<IStaffRole> {
    try {
      const response: AxiosResponse<IStaffRoleResponse> =
        await api.patch<IStaffRoleResponse>(`/admins/roles/${id}`, data);
      return response.data.data;
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.updateRole"));
    }
  }

  /**
   * Delete a StaffRole (super_admin only)
   */
  public async deleteRole(id: string): Promise<void> {
    try {
      await api.delete(`/admins/roles/${id}`);
    } catch (error: any) {
      this.handleError(error, i18n.t("serviceErrors.deleteRole"));
    }
  }

  /**
   * Error handling method
   */
  private handleError(error: any, defaultMessage: string): never {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || defaultMessage);
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
}

const adminServiceInstance = new AdminService();
export default adminServiceInstance;
